import React, { useEffect, useReducer, useCallback, useMemo } from 'react';

import * as dateFns from 'date-fns';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  connectFirestoreEmulator,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import { FIREBASE_API } from 'src/config-global';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import makeApiRequest from 'src/services/api';
import { timestamp } from 'src/utils/firebase';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);

const AUTH = getAuth(firebaseApp);
const DB = getFirestore(firebaseApp);

// Point the Auth instance to the Auth Emulator in development environment
if (process.env.REACT_APP_ENV === 'development') {
  console.log('configuring auth emu');
  connectAuthEmulator(AUTH, 'http://127.0.0.1:9099');
  connectFirestoreEmulator(DB, 'localhost', '8080');
}
// ----------------------------------------------------------------------

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    syncUserIfNeeded(state, action);

    return {
      loading: false,
      user: action.payload.user,
    };
  }

  // set the 2fa resolver
  if (action.type === 'RESOLVER') state = { ...state, ...action.payload };
  // begin the 2fa process
  if (action.type === 'VERIFICATION_ID') state.verificationId = action.payload.verificationId;
  // set the user's input 2fa code
  if (action.type === 'VERIFIER') state.recaptchaVerifier = action.payload.recaptchaVerifier;

  return state;
};

const syncUserIfNeeded = (state, action) => {
  if (!state?.user?.accessToken && action?.payload?.user?.accessToken) {
    const userProfileRef = doc(DB, 'users', action.payload.user.uid);
    const today = timestamp(dateFns.startOfToday());
    const profile = action.payload.user;
    // Attach a snapshot listener to the user's document

    if (profile?.last_sync ? profile?.last_sync < today : true) {
      makeApiRequest(`/api/providers/sync/${profile?.uid}`).then(() => {
        updateDoc(userProfileRef, { last_sync: today }).then(() => {
          console.log('Completed Daily Sync!');
        });
      });
    }
  }
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [initialUser, setInitialUser] = useLocalStorage('initialUser', null);
  const initialState = {
    user: initialUser,
    verificationCode: '',
    loading: true,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  let unsubscribe;
  const initialize = useCallback(() => {
    try {
      onAuthStateChanged(AUTH, async (user) => {
        setInitialUser(user);
        if (user) {
          const userProfileRef = doc(DB, 'users', user.uid);

          // Attach a snapshot listener to the user's document
          unsubscribe = onSnapshot(userProfileRef, (docSnapshot) => {
            const profile = docSnapshot.data();

            dispatch({
              type: 'INITIAL',
              payload: {
                user: {
                  ...user,
                  ...profile,
                  id: user.uid,
                  uid: user.uid,
                  role: 'admin',
                },
              },
            });
          });
        } else {
          dispatch({
            type: 'INITIAL',
            payload: {
              user: null,
            },
          });
        }
      });
    } catch (error) {
      console.error('Firebase auth error: ', error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }

    // Remember to unsubscribe from the listener when not needed anymore
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(AUTH, email, password);
      // Login successful, proceed normally
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(AUTH, error);
        dispatch({
          type: 'RESOLVER',
          payload: {
            resolver,
            needsMFA: true,
          },
        });
      }

      throw error;
      // }
      // } else if (error.code === 'auth/wrong-password') {
      //   console.error('Wrong password');
      // } else {
      //   console.error('Login error:', error);
      // }
    }
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const newUser = await createUserWithEmailAndPassword(AUTH, email, password);

    await sendEmailVerification(newUser.user);

    const userProfile = doc(collection(DB, 'users'), newUser.user?.uid);

    await setDoc(userProfile, {
      uid: newUser.user?.uid,
      user_id: newUser.user?.uid,
      email,
      displayName: `${firstName} ${lastName}`,
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(AUTH, email);
  }, []);

  // REFRESH ACCOUNT
  const refreshAccount = useCallback(async () => {
    try {
      const user = getAuth().currentUser;
      if (user) {
        const userProfile = doc(DB, 'users', user.uid);
        const docSnap = await getDoc(userProfile);
        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...docSnap.data(),
              uid: user.uid,
              id: user.uid,
            },
          },
        });
      }
    } catch (error) {
      console.error('Error refreshing account: ', error);
    }
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const submit2FACode = useCallback(async (code) => {
    const { resolver } = state;
    // if (!resolver) {
    //   console.error('MISSING RESOLVER FOR 2FA');
    //   return;
    // }

    let verificationId;

    // Assuming you're handling the first available method for simplicity
    if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };

      const phoneAuthProvider = new PhoneAuthProvider(AUTH);
      verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        state.recaptchaVerifier
      );
    }

    // try {
    const cred = PhoneAuthProvider.credential(verificationId, code);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await resolver.resolveSignIn(multiFactorAssertion);
    // Login successful, proceed normally
    // } catch (error) {
    //   console.error('2FA error:', error);
    // }
  });

  const setRecaptchaVerifier = (verifier) => {
    dispatch({
      type: 'VERIFIER',
      payload: {
        recaptchaVerifier: verifier,
      },
    });
  };

  const needsMFA = useMemo(() => state.needsMFA, [state]);

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      avatar: faviconForEmail(state.user?.email),
      needsMFA,
      //
      login,
      logout,
      register,
      forgotPassword,
      refreshAccount,
      submit2FACode,
      getAuth: () => AUTH,
      setRecaptchaVerifier,
    }),
    [
      status,
      state.user,
      needsMFA,
      //
      login,
      logout,
      register,
      forgotPassword,
      refreshAccount,
      submit2FACode,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

function faviconForEmail(email = '') {
  switch (email) {
    case 'single@growbread.com':
      return '🙋‍♂️';
    case 'married@growbread.com':
      return '👩‍❤️‍👨';
    case 'emptynester@growbread.com':
      return '🕊️';
    default:
      return '🍞';
  }
}
